// ABOUTME: User profile page where users showcase their professional identity for referral opportunities
// ABOUTME: Profile management interface for referral networking and job opportunities

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { MapPin, Mail, Phone, Edit3, Check, X, Verified, Building, Calendar, User, Star } from 'lucide-react';
const PublicProfile = () => {
  const {
    user,
    profile
  } = useAuth();
  const {
    toast
  } = useToast();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    bio: '',
    location: '',
    contact_email: '',
    contact_phone: '',
    linkedin_url: '',
    current_company: '',
    job_title: '',
    skills: [] as string[],
    seeking_roles: [] as string[],
    actively_seeking: false
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (profile) {
      setFormData({
        bio: profile.bio || '',
        location: profile.location || '',
        contact_email: profile.public_email || '',
        contact_phone: profile.public_phone || '',
        linkedin_url: profile.linkedin_url || '',
        current_company: profile.current_company || '',
        job_title: profile.job_title || '',
        skills: Array.isArray(profile.skills) ? profile.skills : [],
        seeking_roles: Array.isArray(profile.seeking_roles) ? profile.seeking_roles : [],
        actively_seeking: profile.actively_seeking || false
      });
      setLoading(false);
    }
  }, [profile]);
  const updateField = async (field: string, value: any) => {
    if (!user) return;
    const confirmed = window.confirm("Are you sure you want to update this information?");
    if (!confirmed) return;
    try {
      // Map frontend field names to database field names
      const dbField = field === 'contact_email' ? 'public_email' : field === 'contact_phone' ? 'public_phone' : field;
      const {
        error
      } = await supabase.from('profiles').update({
        [dbField]: value
      }).eq('id', user.id);
      if (error) throw error;
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully."
      });
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
      setEditingField(null);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update your profile. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleEdit = (field: string) => {
    setEditingField(field);
  };
  const handleSave = (field: string) => {
    updateField(field, formData[field as keyof typeof formData]);
  };
  const handleCancel = () => {
    setEditingField(null);
    // Reset form data to original values
    if (profile) {
      setFormData({
        bio: profile.bio || '',
        location: profile.location || '',
        contact_email: profile.public_email || '',
        contact_phone: profile.public_phone || '',
        linkedin_url: profile.linkedin_url || '',
        current_company: profile.current_company || '',
        job_title: profile.job_title || '',
        skills: Array.isArray(profile.skills) ? profile.skills : [],
        seeking_roles: Array.isArray(profile.seeking_roles) ? profile.seeking_roles : [],
        actively_seeking: profile.actively_seeking || false
      });
    }
  };
  const userName = profile?.first_name || 'User';
  const userInitials = profile ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}` : user?.email?.[0]?.toUpperCase() || 'U';
  if (loading) {
    return <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 p-6 relative">
      <div className="max-w-4xl mx-auto space-y-6 relative z-10">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            {userName}'s Profile
          </h1>
          <p className="text-gray-600 mt-1">My Personal Internal Profile</p>
        </div>

        {/* Biographic Information Card */}
        <Card className="rounded-xl shadow-sm bg-white border-gray-200 border-l-4 border-l-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Biographic Information
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={profile?.avatar_url} />
                <AvatarFallback>{userInitials}</AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-semibold">
                    {profile?.first_name} {profile?.last_name}
                  </h3>
                  {profile?.verified_status && <Badge variant="secondary" className="bg-green-100 text-green-800">
                      <Verified className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>}
                </div>
                <div className="flex items-center gap-1 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  {editingField === 'location' ? <div className="flex items-center gap-2">
                      <Input value={formData.location} onChange={e => setFormData(prev => ({
                    ...prev,
                    location: e.target.value
                  }))} className="w-48 border-red-500" placeholder="Enter your location" />
                      <Button size="sm" onClick={() => handleSave('location')}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleCancel}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div> : <div className="flex items-center gap-2 group">
                      <span>{formData.location || 'Add your location'}</span>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit('location')} className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <Edit3 className="w-3 h-3" />
                      </Button>
                    </div>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Timeline */}
        <Card className="rounded-xl shadow-sm bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Professional Timeline
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Current Company</label>
                {editingField === 'current_company' ? <div className="flex items-center gap-2 mt-1">
                    <Input value={formData.current_company} onChange={e => setFormData(prev => ({
                  ...prev,
                  current_company: e.target.value
                }))} className="border-red-500" placeholder="Enter your current company" />
                    <Button size="sm" onClick={() => handleSave('current_company')}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancel}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div> : <div className="flex items-center gap-2 group mt-1">
                    <span className="text-gray-900">{formData.current_company || 'Add your current company'}</span>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit('current_company')} className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Edit3 className="w-3 h-3" />
                    </Button>
                  </div>}
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Job Title</label>
                {editingField === 'job_title' ? <div className="flex items-center gap-2 mt-1">
                    <Input value={formData.job_title} onChange={e => setFormData(prev => ({
                  ...prev,
                  job_title: e.target.value
                }))} className="border-red-500" placeholder="Enter your job title" />
                    <Button size="sm" onClick={() => handleSave('job_title')}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancel}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div> : <div className="flex items-center gap-2 group mt-1">
                    <span className="text-gray-900">{formData.job_title || 'Add your job title'}</span>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit('job_title')} className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Edit3 className="w-3 h-3" />
                    </Button>
                  </div>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Refer Me For Section */}
        
      </div>
    </div>;
};
export default PublicProfile;